import { BaseComponent } from '../lib/BaseComponent.js';
import { api } from '../lib/api.js';
import { router } from '../lib/router.js';
import jsQR from 'jsqr';

/**
 * QR Scanner Page Component
 * Uses device camera to scan QR codes on fire extinguishers
 */
export class ScanPage extends BaseComponent {
  private deepLinkHandled = false;
  private stream: MediaStream | null = null;
  private video: HTMLVideoElement | null = null;
  private canvas: HTMLCanvasElement | null = null;
  private canvasContext: CanvasRenderingContext2D | null = null;
  private isScanning = false;
  private cameraFailed = false;
  private scanningAnimationId: number | null = null;
  private errorMessage = '';
  private lastScanTime = 0;
  private scanCooldown = 2000; // 2 seconds between scans

  /**
   * Labels encode a URL (https://fireguardhq.com/scan?code=FG-001) so a plain
   * camera app opens FireGuard; older labels encode the bare code. Accept both.
   */
  static codeFromScan(data: string): string {
    const trimmed = (data || '').trim();
    if (/^https?:\/\//i.test(trimmed)) {
      try {
        const u = new URL(trimmed);
        return (u.searchParams.get('code') || trimmed).trim();
      } catch {
        return trimmed;
      }
    }
    return trimmed;
  }

  protected render(): void {
    this.addStyles(this.getCommonStyles());
    this.addStyles(this.getStyles());

    // Deep link from a printed label: /scan?code=FG-001
    const deepLinked = new URLSearchParams(window.location.search).get('code');
    if (deepLinked && !this.deepLinkHandled) {
      this.deepLinkHandled = true;
      api.getExtinguisherByQR(deepLinked.trim())
        .then((ext) => router.navigate(`/extinguishers/${ext.id}/inspect`))
        .catch(() => {
          this.errorMessage = `No extinguisher found for code ${deepLinked}.`;
          this.shadow.replaceChildren();
          this.render();
        });
    }

    const container = this.createElement('div', { className: 'scan-container' });

    // Header
    const header = this.createElement('header', { className: 'scan-header' });
    const backBtn = this.createElement('button', { className: 'back-btn' }, '← Back');
    backBtn.addEventListener('click', () => router.navigate('/dashboard'));
    const title = this.createElement('h1', {}, 'Scan QR Code');
    header.appendChild(backBtn);
    header.appendChild(title);
    container.appendChild(header);

    // Main content
    const main = this.createElement('main', { className: 'scan-main' });

    // Error message
    if (this.errorMessage) {
      const error = this.createElement('div', { className: 'error-message' }, this.errorMessage);
      main.appendChild(error);
    }

    // Camera view
    const cameraContainer = this.createElement('div', { className: 'camera-container' });

    // Video element for camera stream
    this.video = this.createElement('video', {
      autoplay: 'true',
      playsinline: 'true',
    }) as HTMLVideoElement;
    cameraContainer.appendChild(this.video);

    // Hidden canvas for QR code processing
    this.canvas = this.createElement('canvas', {
      style: 'display: none;'
    }) as HTMLCanvasElement;
    cameraContainer.appendChild(this.canvas);

    // Scan overlay
    const overlay = this.createElement('div', { className: 'scan-overlay' });
    const scanBox = this.createElement('div', { className: 'scan-box' });
    const scanStatus = this.createElement('div', {
      className: 'scan-status',
      id: 'scan-status'
    }, 'Scanning...');
    overlay.appendChild(scanBox);
    overlay.appendChild(scanStatus);
    cameraContainer.appendChild(overlay);

    main.appendChild(cameraContainer);

    // Instructions
    const instructions = this.createElement('div', { className: 'instructions' });
    const instructionTitle = this.createElement('h3', {}, 'How to scan:');
    const instructionList = this.createElement('ul', {});
    const step1 = this.createElement('li', {}, 'Allow camera access when prompted');
    const step2 = this.createElement('li', {}, 'Point camera at QR code on extinguisher');
    const step3 = this.createElement('li', {}, 'Hold steady until code is detected');
    instructionList.appendChild(step1);
    instructionList.appendChild(step2);
    instructionList.appendChild(step3);
    instructions.appendChild(instructionTitle);
    instructions.appendChild(instructionList);
    main.appendChild(instructions);

    // Manual entry option
    const manualEntry = this.createElement('div', { className: 'manual-entry' });
    const manualTitle = this.createElement('p', {}, "Can't scan? Enter QR code manually:");
    const manualForm = this.createElement('form', { className: 'manual-form' });
    manualForm.addEventListener('submit', (e) => this.handleManualEntry(e));

    const input = this.createElement('input', {
      type: 'text',
      placeholder: 'FG-001',
      name: 'qrCode',
    });
    const submitBtn = this.createElement('button', {
      type: 'submit',
      className: 'btn-primary',
    }, 'Look Up');

    manualForm.appendChild(input);
    manualForm.appendChild(submitBtn);
    manualEntry.appendChild(manualTitle);
    manualEntry.appendChild(manualForm);
    main.appendChild(manualEntry);

    container.appendChild(main);
    this.shadow.appendChild(container);

    // Start camera when component is rendered (once; a failed camera must not
    // re-render into an infinite retry loop).
    if (!this.cameraFailed) this.startCamera();
  }

  disconnectedCallback(): void {
    this.isScanning = false;
    this.stream?.getTracks().forEach((t) => t.stop());
    this.stream = null;
  }

  private async startCamera(): Promise<void> {
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } },
      });

      if (this.video && this.canvas) {
        this.video.srcObject = this.stream;
        this.canvasContext = this.canvas.getContext('2d', { willReadFrequently: true });

        // Wait for video to be ready
        this.video.addEventListener('loadedmetadata', () => {
          if (this.video && this.canvas) {
            this.canvas.width = this.video.videoWidth;
            this.canvas.height = this.video.videoHeight;
            this.isScanning = true;
            this.scanQRCode();
          }
        });
      }
    } catch (error) {
      console.error('Camera error:', error);
      this.cameraFailed = true;
      const name = (error as { name?: string } | null)?.name || '';
      this.errorMessage = name === 'NotAllowedError' || name === 'SecurityError'
        ? 'Camera access was blocked. Allow the camera in your browser settings, or type the code from the label below.'
        : 'No camera found on this device. Type the code printed on the label below.';
      this.shadow.innerHTML = '';
      this.render();
    }
  }

  private scanQRCode(): void {
    if (!this.isScanning || !this.video || !this.canvas || !this.canvasContext) {
      return;
    }

    // Check if video has valid dimensions
    if (this.video.readyState === this.video.HAVE_ENOUGH_DATA) {
      this.canvasContext.drawImage(this.video, 0, 0, this.canvas.width, this.canvas.height);
      const imageData = this.canvasContext.getImageData(0, 0, this.canvas.width, this.canvas.height);

      const code = jsQR(imageData.data, imageData.width, imageData.height, {
        inversionAttempts: 'dontInvert',
      });

      if (code && code.data) {
        const now = Date.now();
        // Only process if cooldown has passed
        if (now - this.lastScanTime > this.scanCooldown) {
          this.lastScanTime = now;
          this.handleQRCodeDetected(code.data);
          return; // Stop scanning after successful detection
        }
      }
    }

    // Continue scanning
    this.scanningAnimationId = requestAnimationFrame(() => this.scanQRCode());
  }

  private async handleQRCodeDetected(qrData: string): Promise<void> {
    this.isScanning = false;

    // Update status
    const statusEl = this.shadow.getElementById('scan-status');
    if (statusEl) {
      statusEl.textContent = 'QR Code detected! Looking up...';
      statusEl.style.color = 'var(--success)';
    }

    try {
      const extinguisher = await api.getExtinguisherByQR(ScanPage.codeFromScan(qrData));

      if (statusEl) {
        statusEl.textContent = `Found: ${extinguisher.qr_code}`;
      }

      // Navigate to inspection page after brief delay
      setTimeout(() => {
        router.navigate(`/extinguishers/${extinguisher.id}/inspect`);
      }, 1000);
    } catch (error) {
      console.error('Extinguisher lookup error:', error);
      if (statusEl) {
        statusEl.textContent = 'Extinguisher not found';
        statusEl.style.color = 'var(--danger)';
      }

      // Restart scanning after error
      setTimeout(() => {
        this.isScanning = true;
        this.scanQRCode();
        if (statusEl) {
          statusEl.textContent = 'Scanning...';
          statusEl.style.color = 'white';
        }
      }, 2000);
    }
  }

  private async handleManualEntry(e: Event): Promise<void> {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);
    const qrCode = formData.get('qrCode') as string;

    if (!qrCode) return;

    try {
      const extinguisher = await api.getExtinguisherByQR(qrCode);
      // Navigate to inspection page
      router.navigate(`/extinguishers/${extinguisher.id}/inspect`);
    } catch (error) {
      this.errorMessage = 'Extinguisher not found. Please check the QR code.';
      this.shadow.innerHTML = '';
      this.render();
    }
  }

  protected cleanup(): void {
    // Stop scanning
    this.isScanning = false;
    if (this.scanningAnimationId !== null) {
      cancelAnimationFrame(this.scanningAnimationId);
      this.scanningAnimationId = null;
    }

    // Stop camera stream when component is removed
    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
      this.stream = null;
    }
  }

  private getStyles(): string {
    return `
      .scan-container {
        min-height: 100vh;
        background: var(--gray-900);
        color: white;
      }

      .scan-header {
        padding: var(--spacing-md) var(--spacing-lg);
        background: rgba(0, 0, 0, 0.5);
        display: flex;
        align-items: center;
        gap: var(--spacing-md);
      }

      .back-btn {
        background: transparent;
        border: 1px solid white;
        color: white;
        padding: 8px 16px;
        border-radius: var(--border-radius);
        font-size: 14px;
        cursor: pointer;
        transition: background 0.2s;
      }

      .back-btn:hover {
        background: rgba(255, 255, 255, 0.1);
      }

      .scan-header h1 {
        font-size: 20px;
        font-weight: 600;
        margin: 0;
      }

      .scan-main {
        max-width: 800px;
        margin: 0 auto;
        padding: var(--spacing-lg);
      }

      .error-message {
        background: rgba(239, 68, 68, 0.2);
        border: 1px solid var(--danger);
        color: white;
        padding: var(--spacing-md);
        border-radius: var(--border-radius);
        margin-bottom: var(--spacing-md);
      }

      .camera-container {
        position: relative;
        width: 100%;
        max-width: 500px;
        margin: 0 auto var(--spacing-lg);
        border-radius: var(--border-radius);
        overflow: hidden;
        background: black;
      }

      video {
        width: 100%;
        height: auto;
        display: block;
      }

      .scan-overlay {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        background: rgba(0, 0, 0, 0.3);
      }

      .scan-box {
        width: 250px;
        height: 250px;
        border: 3px solid white;
        border-radius: var(--border-radius);
        box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.5);
        animation: pulse 2s ease-in-out infinite;
      }

      @keyframes pulse {
        0%, 100% {
          opacity: 1;
        }
        50% {
          opacity: 0.5;
        }
      }

      .scan-status {
        position: absolute;
        bottom: 20px;
        left: 50%;
        transform: translateX(-50%);
        background: rgba(0, 0, 0, 0.8);
        color: white;
        padding: 12px 24px;
        border-radius: var(--border-radius);
        font-size: 16px;
        font-weight: 500;
        text-align: center;
        min-width: 200px;
      }

      .instructions {
        background: rgba(255, 255, 255, 0.1);
        border-radius: var(--border-radius);
        padding: var(--spacing-lg);
        margin-bottom: var(--spacing-lg);
      }

      .instructions h3 {
        font-size: 18px;
        margin-bottom: var(--spacing-md);
      }

      .instructions ul {
        list-style: none;
        padding: 0;
      }

      .instructions li {
        padding: var(--spacing-sm) 0;
        padding-left: 24px;
        position: relative;
      }

      .instructions li::before {
        content: '✓';
        position: absolute;
        left: 0;
        color: var(--success);
      }

      .manual-entry {
        background: rgba(255, 255, 255, 0.1);
        border-radius: var(--border-radius);
        padding: var(--spacing-lg);
      }

      .manual-entry p {
        margin-bottom: var(--spacing-md);
        font-size: 14px;
      }

      .manual-form {
        display: flex;
        flex-wrap: wrap;
        gap: var(--spacing-md);
      }

      .manual-form input {
        flex: 1 1 160px;
        /* Without this an input refuses to shrink below its intrinsic size, and the
           input + gap + button overflowed a 393px viewport by about 11px. */
        min-width: 0;
        padding: 12px;
        border: 1px solid rgba(255, 255, 255, 0.3);
        border-radius: var(--border-radius);
        background: rgba(255, 255, 255, 0.1);
        color: white;
        font-size: 16px;
      }

      .manual-form input::placeholder {
        color: rgba(255, 255, 255, 0.5);
      }

      .manual-form input:focus {
        outline: none;
        border-color: white;
      }

      .btn-primary {
        padding: 12px 24px;
        background: var(--primary);
        color: white;
        border: none;
        border-radius: var(--border-radius);
        font-size: 16px;
        font-weight: 500;
        cursor: pointer;
        transition: background 0.2s;
      }

      .btn-primary:hover {
        background: var(--primary-dark);
      }

      /* Narrow phones: shrink the padding that was squeezing the manual-entry
         form, and let its input collapse so the row never forces a sideways
         scroll. This must come last to beat the base rules above. */
      @media (max-width: 640px) {
        .scan-main { padding: var(--spacing-md); }
        .manual-entry { padding: var(--spacing-md); }
        .instructions { padding: var(--spacing-md); }
        .scan-status { min-width: 0; padding: 10px 16px; }
        .scan-box { width: min(250px, 70vw); }
        .manual-form input { flex: 1 1 100%; }
        .manual-form button { flex: 1 1 100%; }
      }
    `;
  }
}

// Register custom element
customElements.define('scan-page', ScanPage);
